import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Swords } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api, getToken } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const SEEN_KEY = "fc_seen_challenges";
const loadSeen = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY)) || []);
  } catch {
    return new Set();
  }
};

// Polls for incoming challenges and pops a toast + bell badge the moment one arrives.
const ChallengeBell = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [incoming, setIncoming] = useState([]);
  const seenRef = useRef(loadSeen());

  useEffect(() => {
    const poll = async () => {
      if (!getToken()) {
        setIncoming([]);
        return;
      }
      try {
        const list = await api.incomingMatches();
        setIncoming(list);

        // Toast once for any challenge id we've never seen before.
        const fresh = list.filter((m) => !seenRef.current.has(m.id));
        if (fresh.length) {
          fresh.forEach((m) =>
            toast({
              title: "New challenge!",
              description: `${m.creator.username} wants to wager £${m.stake}. Tap the bell to accept.`,
            })
          );
          list.forEach((m) => seenRef.current.add(m.id));
          localStorage.setItem(SEEN_KEY, JSON.stringify([...seenRef.current]));
        }
      } catch (e) {
        /* ignore */
      }
    };

    poll();
    const t = setInterval(poll, 6000);
    return () => clearInterval(t);
  }, [toast]);

  if (!getToken()) return null;

  const count = incoming.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative pill-nav rounded-full p-2.5 text-slate-200" title="Challenges">
          <Bell className="w-4 h-4" />
          {count > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#10B981] text-[#0F172A] text-[10px] font-bold flex items-center justify-center">
              {count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 bg-[#1E293B] border border-white/10 text-white p-3"
      >
        <div className="text-sm font-semibold mb-2">Challenge invites</div>
        {count === 0 ? (
          <div className="text-muted-38 text-sm py-4 text-center">No challenges waiting.</div>
        ) : (
          <div className="space-y-2">
            {incoming.map((m) => (
              <button
                key={m.id}
                onClick={() => navigate(`/multiplayer/match/${m.id}`)}
                className="w-full flex items-center gap-3 rounded-lg border border-[#38BDF8]/30 bg-[#38BDF8]/10 px-3 py-2 text-left hover:bg-[#38BDF8]/15 transition-colors"
              >
                <span className="w-8 h-8 rounded-md bg-[#38BDF8]/20 flex items-center justify-center shrink-0">
                  <Swords className="w-4 h-4 text-[#38BDF8]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{m.creator.username}</span>
                  <span className="block text-muted-38 text-xs">Stake {m.stake} · Win {m.pot}</span>
                </span>
                <span className="rounded-md bg-[#38BDF8] text-white text-xs font-semibold px-3 py-1 shrink-0">Accept</span>
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ChallengeBell;
